/**
 * THE ONESCENT DIAL - Main Dial Component
 * Formerly: CosmicClock.js
 * 
 * Renamed for code-level congruence with branding meta.
 * This is the primary visual instrument displaying astronomical data.
 */
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import ZodiacRing from './ZodiacRing';
import PlanetaryHand from './PlanetaryHand';
import SeasonsRing from './SeasonsRing';
import SabbatsRing from './SabbatsRing';
import AspectLines from './AspectLines';
import { GearTeeth, Rivets, EngravedRing, MechanicalSpokes } from './MechanicalElements';
import { BRANDING_META } from '../utils/styleCard';
import { PLANET_CONFIG, longitudeToPosition } from './clockUtils';
import { useResponsive } from '../hooks/useResponsive';

const Dial = ({ data, onPlanetClick, onElementClick, onCenterClick, hideHints = false }) => {
  // State for hovered planet label (rendered on top of everything)
  const [hoveredPlanet, setHoveredPlanet] = useState(null);
  const { isMobile } = useResponsive();
  // Base size for viewBox - SVG scales proportionally
  // Using larger base (1200) for better zoom quality and sharper rendering
  const size = 1200;
  const center = size / 2;
  
  // Ring sizing (relative to viewBox) - scaled up proportionally for 1200px viewBox
  const seasonOuterRadius = 580;
  const sabbatOuterRadius = 516;
  const sabbatInnerRadius = 472;
  
  // Condensed central zodiac disc - extended inward for more planetary space
  const zodiacRadius = 240;
  const zodiacInnerRadius = zodiacRadius - 90;  // Extended inward (was -45)
  
  // Planet zones
  const innerZoneMax = zodiacInnerRadius - 20;
  const edgeZoneRadius = zodiacRadius + 30;
  const outerZoneMin = zodiacRadius + 80;
  const outerZoneMax = sabbatInnerRadius - 16;
  
  // Early return if no data
  if (!data) return null;
  
  // Get location intelligence for hemisphere-aware rendering
  const locationIntelligence = data.location_intelligence || data.season?.location_info || {};
  const isSouthernHemisphere = locationIntelligence.is_southern || locationIntelligence.hemisphere === 'south';
  const isPolar = locationIntelligence.is_polar || locationIntelligence.climate_zone === 'polar';
  const isEquatorial = locationIntelligence.is_equatorial || locationIntelligence.climate_zone === 'equatorial';

  // Index upcoming ingresses by planet so we can show a proximity indicator on each bead.
  // Only the most imminent ingress per planet is kept (sorted ascending by days_until upstream).
  const ingressByPlanet = {};
  const upcomingIngresses = data.quick_nav?.upcoming_ingresses || [];
  for (const ing of upcomingIngresses) {
    if (ing && ing.planet && !(ing.planet in ingressByPlanet)) {
      ingressByPlanet[ing.planet] = ing;
    }
  }
  
  const handleElementClick = (type, key) => {
    if (onElementClick) {
      onElementClick(type, key);
    }
  };

  // Handle center hub click - opens Quick Status overlay
  const handleCenterClick = (e) => {
    e.stopPropagation();
    if (onCenterClick) {
      onCenterClick();
    }
  };

  return (
    <div 
      className="dial-wrapper dial-container"
      data-testid={BRANDING_META.testIds.dial}
    >
      <svg
        viewBox={`0 0 ${size} ${size}`}
        className="dial-svg"
        width="100%"
        height="100%"
        preserveAspectRatio="xMidYMid meet"
        data-testid={BRANDING_META.testIds.dialSvg}
        style={{
          // Enhanced rendering for zoom quality
          shapeRendering: 'geometricPrecision',
          textRendering: 'optimizeLegibility'
        }}
      >
        <defs>
          {/* Enhanced gradients for better quality at zoom */}
          <radialGradient id="dialFaceGradient" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#0B0F19" />
            <stop offset="70%" stopColor="#050814" />
            <stop offset="100%" stopColor="#020408" />
          </radialGradient>
          <radialGradient id="spaceGradient" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#0a0d14" />
            <stop offset="50%" stopColor="#060810" />
            <stop offset="100%" stopColor="#03050a" />
          </radialGradient>
          {/* Metallic gradient for gear elements */}
          <linearGradient id="brassGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#D4A574" />
            <stop offset="50%" stopColor="#C5A059" />
            <stop offset="100%" stopColor="#8B6914" />
          </linearGradient>
          {/* Center hub glow for interactivity hint */}
          <radialGradient id="centerHubGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#C5A059" stopOpacity="0.3" />
            <stop offset="100%" stopColor="#C5A059" stopOpacity="0" />
          </radialGradient>
          {/* Southern Hemisphere Shadow */}
          <linearGradient id="southernShadow" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="transparent" />
            <stop offset="30%" stopColor="rgba(10,15,35,0.2)" />
            <stop offset="60%" stopColor="rgba(10,15,35,0.35)" />
            <stop offset="100%" stopColor="rgba(10,15,35,0.45)" />
          </linearGradient>
          <clipPath id="lowerHalfClip">
            <rect x="0" y={center} width={size} height={center} />
          </clipPath>
        </defs>

        {/* Background circle */}
        <circle
          cx={center}
          cy={center}
          r={590}
          fill="url(#dialFaceGradient)"
          stroke="#C5A059"
          strokeWidth="5"
        />
        
        {/* Southern Hemisphere Shadow (Lunar Dominant - below horizon) */}
        <circle
          cx={center}
          cy={center}
          r={580}
          fill="url(#southernShadow)"
          clipPath="url(#lowerHalfClip)"
          opacity="1"
          style={{ pointerEvents: 'none' }}
        />
        
        {/* Quarter-Day Crosshairs - Horizon and Meridian Lines */}
        <line
          x1={center - 588}
          y1={center}
          x2={center + 588}
          y2={center}
          stroke="#C5A059"
          strokeWidth="3"
          opacity="0.4"
          strokeDasharray="24 12"
        />
        <line
          x1={center}
          y1={center - 588}
          x2={center}
          y2={center + 588}
          stroke="#C5A059"
          strokeWidth="3"
          opacity="0.4"
          strokeDasharray="24 12"
        />
        
        {/* Cardinal Point Labels - enhanced font size for zoom quality */}
        <text x={center - 575} y={center + 7} fill="#C5A059" fontSize="20" opacity="0.6" fontFamily="serif" fontWeight="bold">
          {isSouthernHemisphere ? 'W' : 'E'}
        </text>
        <text x={center + 563} y={center + 7} fill="#C5A059" fontSize="20" opacity="0.6" fontFamily="serif" fontWeight="bold">
          {isSouthernHemisphere ? 'E' : 'W'}
        </text>
        <text x={center - 7} y={center - 563} fill="#C5A059" fontSize="20" opacity="0.6" fontFamily="serif" fontWeight="bold">N</text>
        <text x={center - 6} y={center + 580} fill="#C5A059" fontSize="20" opacity="0.6" fontFamily="serif" fontWeight="bold">S</text>
        
        {/* Outer gear teeth */}
        <GearTeeth
          center={center}
          radius={584}
          teethCount={96}
          toothHeight={12}
          toothWidth={6}
          color="#6B5510"
          opacity={0.6}
        />
        
        {/* Seasons Ring */}
        <SeasonsRing
          center={center}
          radius={seasonOuterRadius}
          season={{
            ...data.season,
            dominant_phase: data.cih_intelligence?.dominant_phase,
            cih_phase: data.cih_intelligence?.location_phase_foundation?.phase_vector?.primary_phase
          }}
          sunLongitude={data.positions?.sun?.longitude}
          onSeasonClick={handleElementClick}
          locationIntelligence={locationIntelligence}
          isEquatorialZone={isEquatorial}
        />
        
        {/* Decorative rivets on seasons ring */}
        <Rivets center={center} radius={seasonOuterRadius - 28} count={24} size={5} />
        
        {/* Sabbats Ring - now shown for all locations since all have four-phase cycles */}
        <SabbatsRing
          center={center}
          radius={sabbatOuterRadius}
          sabbats={data.sabbats}
          currentDate={new Date(data.timestamp)}
          onSabbatClick={handleElementClick}
          locationIntelligence={locationIntelligence}
        />
        
        {/* Gear teeth between sabbats and space */}
        <GearTeeth
          center={center}
          radius={sabbatInnerRadius - 4}
          teethCount={64}
          toothHeight={10}
          toothWidth={7}
          color="#8B6914"
          opacity={0.5}
        />
        
        {/* The "Space" - dark cosmic gap */}
        <circle
          cx={center}
          cy={center}
          r={sabbatInnerRadius - 16}
          fill="url(#spaceGradient)"
          style={{ pointerEvents: 'none' }}
        />
        
        {/* Engraved decorative rings in space */}
        <EngravedRing center={center} radius={400} strokeWidth={1} dashed={true} />
        <EngravedRing center={center} radius={340} strokeWidth={1} dashed={true} />
        
        {/* Subtle orbital guide rings */}
        <circle
          cx={center}
          cy={center}
          r={edgeZoneRadius}
          fill="none"
          stroke="#C5A059"
          strokeWidth="1"
          opacity="0.2"
          strokeDasharray="8 15"
          style={{ pointerEvents: 'none' }}
        />
        
        {/* Gear teeth around zodiac disc */}
        <GearTeeth
          center={center}
          radius={zodiacRadius}
          teethCount={48}
          toothHeight={16}
          toothWidth={12}
          color="#A08040"
          opacity={0.7}
        />
        
        {/* Central Zodiac Disc */}
        <ZodiacRing 
          center={center}
          radius={zodiacRadius}
          currentSign={data.positions?.sun?.zodiac_sign}
          onZodiacClick={handleElementClick}
          isSouthernHemisphere={isSouthernHemisphere}
          isEquatorialZone={isEquatorial}
        />
        
        {/* Mechanical spokes from center */}
        <MechanicalSpokes 
          center={center} 
          innerRadius={40} 
          outerRadius={zodiacInnerRadius - 10} 
          count={12} 
          width={3}
        />
        
        {/* Aspect Lines */}
        <g transform={`translate(${center}, ${center})`}>
          <AspectLines
            aspects={data.aspects}
            positions={data.positions}
            innerZoneMax={innerZoneMax}
            edgeZoneRadius={edgeZoneRadius}
            outerZoneMin={outerZoneMin}
            outerZoneMax={outerZoneMax}
          />
        </g>
        
        {/* Faint orbit paths for all planetary zones */}
        <g style={{ pointerEvents: 'none' }}>
          {/* Inner zone orbits (Moon, Mercury, Venus) */}
          <circle cx={center} cy={center} r={innerZoneMax * 0.35} fill="none" 
            stroke="#C5A059" strokeWidth="1" opacity="0.15" strokeDasharray="4 8" />
          <circle cx={center} cy={center} r={innerZoneMax * 0.65} fill="none" 
            stroke="#C5A059" strokeWidth="1" opacity="0.15" strokeDasharray="4 8" />
          <circle cx={center} cy={center} r={innerZoneMax * 0.95} fill="none" 
            stroke="#C5A059" strokeWidth="1" opacity="0.15" strokeDasharray="4 8" />
          
          {/* Edge zone orbit (Sun) */}
          <circle cx={center} cy={center} r={edgeZoneRadius} fill="none" 
            stroke="#FFD700" strokeWidth="1.5" opacity="0.2" strokeDasharray="6 10" />
          
          {/* Outer zone orbits (Mars, Jupiter, Saturn, Uranus, Neptune, Pluto) */}
          <circle cx={center} cy={center} r={outerZoneMin + (outerZoneMax - outerZoneMin) * 0.15} fill="none" 
            stroke="#C5A059" strokeWidth="1" opacity="0.12" strokeDasharray="4 8" />
          <circle cx={center} cy={center} r={outerZoneMin + (outerZoneMax - outerZoneMin) * 0.35} fill="none" 
            stroke="#C5A059" strokeWidth="1" opacity="0.12" strokeDasharray="4 8" />
          <circle cx={center} cy={center} r={outerZoneMin + (outerZoneMax - outerZoneMin) * 0.55} fill="none" 
            stroke="#C5A059" strokeWidth="1" opacity="0.12" strokeDasharray="4 8" />
          <circle cx={center} cy={center} r={outerZoneMin + (outerZoneMax - outerZoneMin) * 0.72} fill="none" 
            stroke="#C5A059" strokeWidth="1" opacity="0.12" strokeDasharray="4 8" />
          <circle cx={center} cy={center} r={outerZoneMin + (outerZoneMax - outerZoneMin) * 0.88} fill="none" 
            stroke="#C5A059" strokeWidth="1" opacity="0.12" strokeDasharray="4 8" />
        </g>
        
        {/* Planet Hands - on top */}
        <g transform={`translate(${center}, ${center})`}>
          {data.positions && Object.entries(data.positions).map(([planet, pos]) => (
            <PlanetaryHand
              key={planet}
              planet={planet}
              longitude={pos.longitude}
              zodiacSign={pos.zodiac_sign}
              isRetrograde={pos.is_retrograde}
              stationStatus={pos.station_status}
              innerZoneMax={innerZoneMax}
              edgeZoneRadius={edgeZoneRadius}
              outerZoneMin={outerZoneMin}
              outerZoneMax={outerZoneMax}
              onClick={() => onPlanetClick && onPlanetClick(planet)}
              isSouthernHemisphere={isSouthernHemisphere}
              isEquatorialZone={isEquatorial}
              onHoverChange={(isHovered) => setHoveredPlanet(isHovered ? planet : null)}
              showLabel={false}
              ingressInfo={ingressByPlanet[planet] || null}
              isMobile={isMobile}
            />
          ))}
        </g>
        
        {/* Center Hub - Interactive, opens Quick Status */}
        <g 
          style={{ cursor: 'pointer' }}
          onClick={handleCenterClick}
          data-testid={BRANDING_META.testIds.dialCenter}
        >
          {/* Hover glow area */}
          <motion.circle
            cx={center}
            cy={center}
            r={45}
            fill="url(#centerHubGlow)"
            initial={{ opacity: 0 }}
            whileHover={{ opacity: 1, scale: 1.2 }}
            transition={{ duration: 0.2 }}
          />
          {/* Outer hub ring */}
          <circle
            cx={center}
            cy={center}
            r={36}
            fill="#0B0F19"
            stroke="url(#brassGradient)"
            strokeWidth="7"
          />
          {/* Inner hub detail */}
          <circle
            cx={center}
            cy={center}
            r={21}
            fill="#8B6914"
            stroke="#5C4A1F"
            strokeWidth="2"
          />
          {/* Center rivet with highlight */}
          <circle
            cx={center}
            cy={center}
            r={9}
            fill="#C5A059"
          />
          <circle
            cx={center - 2}
            cy={center - 2}
            r={3}
            fill="#E8D9B0"
            opacity="0.7"
          />
          {/* Quick View hint - only show when not in split view */}
          {!hideHints && (
            <text
              x={center}
              y={center + 10}
              textAnchor="middle"
              fontSize="36"
              fontWeight="bold"
              fill="#0B0F19"
              opacity="0.6"
              style={{ pointerEvents: 'none', fontFamily: 'serif' }}
            >
              ?
            </text>
          )}
        </g>
        
        {/* Planet Hover Label Overlay - rendered LAST to appear on top of everything */}
        {hoveredPlanet && data.positions?.[hoveredPlanet] && (() => {
          const pos = data.positions[hoveredPlanet];
          const config = PLANET_CONFIG[hoveredPlanet] || { zone: 'outer', zonePosition: 0.5, color: '#C5A059' };
          const handLength = config.zone === 'inner' 
            ? 30 + config.zonePosition * (innerZoneMax - 30)
            : config.zone === 'edge' 
              ? edgeZoneRadius 
              : outerZoneMin + config.zonePosition * (outerZoneMax - outerZoneMin);
          // Only apply 180° offset for southern TEMPERATE hemisphere
          // Equatorial zones show true planetary positions without offset
          const applyOffset = isSouthernHemisphere && !isEquatorial;
          const { x: endX, y: endY } = longitudeToPosition(pos.longitude, handLength, applyOffset);
          const beadRadius = config.isSun ? 36 : 27;
          
          const planetNames = {
            sun: 'Sun', moon: 'Moon', mercury: 'Mercury', venus: 'Venus', mars: 'Mars',
            jupiter: 'Jupiter', saturn: 'Saturn', uranus: 'Uranus', neptune: 'Neptune', pluto: 'Pluto',
            chiron: 'Chiron', north_node: 'North Node', south_node: 'South Node', asc: 'Ascendant', mc: 'Midheaven'
          };
          const planetName = planetNames[hoveredPlanet] || hoveredPlanet.charAt(0).toUpperCase() + hoveredPlanet.slice(1);
          
          return (
            <g transform={`translate(${center + endX}, ${center + endY - beadRadius - 30})`} style={{ pointerEvents: 'none' }}>
              <rect
                x={-75}
                y={-24}
                width={150}
                height={48}
                rx={8}
                fill="rgba(0, 0, 0, 0.95)"
                stroke={config.color}
                strokeWidth={3}
                style={{ filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.8))' }}
              />
              <text
                x={0}
                y={2}
                textAnchor="middle"
                dominantBaseline="central"
                fill="#fff"
                fontSize={32}
                fontWeight="700"
                fontFamily="'Cinzel', serif"
                style={{ textRendering: 'optimizeLegibility' }}
              >
                {planetName}
              </text>
            </g>
          );
        })()}
      </svg>
      
      {/* Click hint - only show when not in split view */}
      {!hideHints && (
        <div className="dial-hint">
          Select any Dial element for more information
        </div>
      )}
    </div>
  );
};

export default Dial;
